<template>
    <div class="row">
        <div class="col-md-9">{{ room.name }}</div>
        <div v-if="hasAnyOrderApplication" class="col-md-3">
            <button
                v-if="isActivateAllAllowed"
                class="btn btn-sm btn-clean pull-right"
                :disabled="isButtonDisabled"
                @click="activateOfferForAll"
            >
                {{ i18n("offer.activate") }}
            </button>

            <button
                v-if="!isActivateAllAllowed"
                class="btn btn-sm btn-clean pull-right"
                :disabled="isButtonDisabled"
                @click="deactivateOfferForAll"
            >
                {{ i18n("offer.deactivate") }}
            </button>
        </div>
    </div>
    <RoomOrderApplicationsList
        v-if="hasMultipleOrderApplications"
        :room="room"
        :offer="offer"
        :disabled="isButtonDisabled"
        @onActivateClick="(orderApp) => activateOffer(orderApp)"
        @onDeactivateClick="(orderApp) => deactivateOffer(orderApp)"
    />
</template>

<script setup lang="ts">
    import { Factory } from "@/data/dao/Factory";
    import { CatalogDao } from "@/data/dao/service/CatalogDao";
    import type { IOfferWithScheduleState } from "@/data/model/Offer";
    import type { IRoomWithService } from "@/data/model/Room";
    import type { IRoomOrderApplication } from "@/data/model/Room";
    import { isOfferActivated } from "@/data/services/OfferPlanningService";
    import { i18n } from "@/portal/Trans";
    import { UiNotification } from "@/portal/ui/Notification";
    import { logError } from "@/shared/logging/errors";
    import { computed, ref } from "vue";

    import RoomOrderApplicationsList from "./RoomOrderApplicationsList.vue";

    const catalogDao = Factory.getInstance<CatalogDao>(CatalogDao);

    const props = defineProps<{
        room: IRoomWithService;
        offer: IOfferWithScheduleState;
    }>();

    const emit = defineEmits(["offerUpdated"]);

    const isButtonDisabled = ref(false);

    const roomOrderApplications = computed(() => {
        const allOrderApplications = [
            ...props.room.endUserServices.filter((x) => x.orderUseCase?.id).map((x) => x.orderUseCase!),
            ...props.room.operatorServices.filter((x) => x.orderUseCase?.id).map((x) => x.orderUseCase!),
        ];

        // get only unique ids
        const result = allOrderApplications.filter(
            (app, index) => index === allOrderApplications.findIndex((other) => app.id === other.id)
        );

        return result;
    });

    const hasAnyOrderApplication = computed(() => roomOrderApplications.value.length > 0);
    const hasMultipleOrderApplications = computed(() => roomOrderApplications.value.length > 1);
    const isActivateAllAllowed = computed(() => {
        // activate all is allowed if not all order applications have active offer
        for (const orderApplication of roomOrderApplications.value) {
            if (isOfferActivated(props.offer, props.room.id, orderApplication.id)) {
                return false;
            }
        }

        return true;
    });

    async function activateOfferForAll(): Promise<void> {
        try {
            isButtonDisabled.value = true;
            const updatedOffer = await catalogDao.activateOfferForAllOrderApplications(props.offer.id, props.room.id);
            emit("offerUpdated", updatedOffer);
            UiNotification.showSuccess(i18n("modal.offerRoomActivation.successfulActivation"));
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("modal.offerRoomActivation.failedActivation"));
        } finally {
            isButtonDisabled.value = false;
        }
    }

    async function deactivateOfferForAll(): Promise<void> {
        try {
            isButtonDisabled.value = true;
            const updatedOffer = await catalogDao.deactivateOfferAllOrderApplications(props.offer.id, props.room.id);
            emit("offerUpdated", updatedOffer);
            UiNotification.showSuccess(i18n("modal.offerRoomActivation.sucessfulDeactivation"));
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("modal.offerRoomActivation.failedDeactivation"));
        } finally {
            isButtonDisabled.value = false;
        }
    }

    async function activateOffer(orderApp: IRoomOrderApplication): Promise<void> {
        try {
            isButtonDisabled.value = true;
            const updatedOffer = await catalogDao.activateOfferForOrderApplication(
                props.offer.id,
                props.room.id,
                orderApp.id
            );
            emit("offerUpdated", updatedOffer);
            UiNotification.showSuccess(i18n("modal.offerRoomActivation.successfulActivation"));
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("modal.offerRoomActivation.failedActivation"));
        } finally {
            isButtonDisabled.value = false;
        }
    }

    async function deactivateOffer(orderApp: IRoomOrderApplication): Promise<void> {
        try {
            isButtonDisabled.value = true;
            const updatedOffer = await catalogDao.deactivateOfferForOrderApplication(
                props.offer.id,
                props.room.id,
                orderApp.id
            );
            emit("offerUpdated", updatedOffer);
            UiNotification.showSuccess(i18n("modal.offerRoomActivation.sucessfulDeactivation"));
        } catch (ex) {
            logError(ex);
            UiNotification.showException(i18n("modal.offerRoomActivation.failedDeactivation"));
        } finally {
            isButtonDisabled.value = false;
        }
    }
</script>

<style lang="scss"></style>
